import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import './PromoBanner.css';

const PRE_SEASON_SLOTS = [
  { start: '2026-08-01T00:00:00', end: '2026-08-08T00:00:00', label: 'Cycle 1' },
  { start: '2026-08-08T00:00:00', end: '2026-08-15T00:00:00', label: 'Cycle 2' },
  { start: '2026-08-15T00:00:00', end: '2026-08-22T00:00:00', label: 'Cycle 3' },
  { start: '2026-08-22T00:00:00', end: '2026-08-29T00:00:00', label: 'Cycle 4' },
  { start: '2026-08-29T00:00:00', end: '2026-08-31T23:59:59', label: 'Final Cycle' }
];

function getScheduledPreSeasonPromo() {
  const now = new Date();
  const nowTime = now.getTime();

  for (const slot of PRE_SEASON_SLOTS) {
    const endTime = new Date(slot.end).getTime();

    if (nowTime < endTime) {
      return {
        isActive: true,
        prefix: 'PRE-SEASON OFFER:',
        highlight: '$25 OFF',
        text: 'Use club code at checkout · Ships local from Canada 🍁',
        countdownEnd: slot.end,
        bgColor: '#fff501',
        prefixColor: '#000000',
        highlightColor: '#ff007f',
        textColor: '#000000',
        timerColor: '#000000',
        linkUrl: '/#product-showcase',
        slotLabel: slot.label
      };
    }
  }
  return null;
}

export default function PromoBanner() {
  const [dbPromo, setDbPromo] = useState(null);
  const [activePromo, setActivePromo] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Track screen size so text and timer can match on mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let unsub = () => {};
    try {
      unsub = onSnapshot(
        doc(db, 'siteSettings', 'promo'), 
        (docSnap) => {
          if (docSnap.exists()) {
            setDbPromo(docSnap.data());
          } else {
            setDbPromo(null);
          }
        },
        (err) => {
          console.warn('Firestore promo listener warning:', err);
        }
      );
    } catch (e) {
      console.warn('Firestore promo catch:', e);
    }
    return () => unsub();
  }, []);

  // Compute active promo state (Firestore override takes precedence over recurring schedule)
  useEffect(() => {
    const computePromo = () => {
      // If Firestore explicitly sets isActive to false, hide the banner completely
      if (dbPromo && dbPromo.isActive === false) {
        setActivePromo(null);
        return;
      }

      if (dbPromo && dbPromo.overrideManual && dbPromo.isActive) {
        setActivePromo(dbPromo);
      } else {
        const scheduled = getScheduledPreSeasonPromo();
        if (scheduled) {
          setActivePromo(scheduled);
        } else if (dbPromo && dbPromo.isActive) {
          setActivePromo(dbPromo);
        } else {
          setActivePromo(null);
        }
      }
    };

    computePromo();
    const interval = setInterval(computePromo, 10000);
    return () => clearInterval(interval);
  }, [dbPromo]);

  useEffect(() => {
    if (!activePromo || !activePromo.isActive || !activePromo.countdownEnd) return;
    
    const targetDate = new Date(activePromo.countdownEnd).getTime();
    
    const updateTimer = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      });
    };

    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [activePromo]);

  if (!activePromo || !activePromo.isActive) {
    return (
      <div className="announcement-bar flex-center">
        <p className="announcement-text">
          ⚽ USE CLUB CODE TO GET $30 OFF PLAYERMAKER. FREE SHIPPING CANADA WIDE 🍁
        </p>
      </div>
    );
  }

  const bannerStyle = {
    fontFamily: activePromo.fontFamily && activePromo.fontFamily !== 'inherit' ? activePromo.fontFamily : 'var(--font-heading)',
    fontSize: activePromo.fontSize ? `${activePromo.fontSize}px` : '1.35rem'
  };

  const content = (
    <div className="promo-banner-content" style={bannerStyle}>
      <div className="promo-text-wrap">
        <div className="promo-headline">
          <span className="promo-prefix" style={{ color: activePromo.prefixColor || '#000000' }}>{activePromo.prefix}</span>
          {' '}
          <span className="promo-highlight" style={{ color: activePromo.highlightColor || '#ff0055' }}>{activePromo.highlight}</span>
        </div>
        {activePromo.text && (
          <div className="promo-subtext" style={{ color: activePromo.textColor || '#000000' }}>
            {activePromo.text}
          </div>
        )}
      </div>
      
      {activePromo.countdownEnd && <div className="promo-divider" aria-hidden="true" />}
      
      {activePromo.countdownEnd && (
        <div className="promo-timer" style={{
          color: activePromo.timerColor || '#000000',
          fontSize: isMobile
            ? (activePromo.fontSize ? `${activePromo.fontSize}px` : '1.35rem')
            : (activePromo.timerFontSize ? `${activePromo.timerFontSize}px` : '28px')
        }}>
          <div className="timer-block">
            <span className="timer-val">{String(timeLeft.days).padStart(2, '0')}</span>
            <span className="timer-label">Days</span>
          </div>
          <span className="timer-sep">:</span>
          <div className="timer-block">
            <span className="timer-val">{String(timeLeft.hours).padStart(2, '0')}</span>
            <span className="timer-label">Hours</span>
          </div>
          <span className="timer-sep">:</span>
          <div className="timer-block">
            <span className="timer-val">{String(timeLeft.minutes).padStart(2, '0')}</span>
            <span className="timer-label">Min</span>
          </div>
          <span className="timer-sep">:</span>
          <div className="timer-block">
            <span className="timer-val">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="timer-label">Sec</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="promo-banner" style={{ backgroundColor: activePromo.bgColor || '#fff501' }}>
      {activePromo.linkUrl ? (
        <a href={activePromo.linkUrl} className="promo-banner-link">
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  );
}
